import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';

import appendFetchMorePage from '../utils/appendFetchMorePage';

const PAGE_SIZE = 30;
const SEARCH_DELAY_MS = 300;

/**
 * Server search with paging for a slide-up list: the typed search is sent as
 * `filter.query` after a short pause, a new search starts again from page 0
 * and loadMore() (FlatList onEndReached) appends the next page.
 * Params:
 * - document: paged query taking $page, $perPage and $filter
 * - field: its result field ({ items, count })
 * - filter: the fixed filter the search is added to
 * - isActive: false skips the query (list closed)
 * Returns search / setSearch (input value), query (the search sent), resetSearch(),
 * list (the current or, while the next one loads, previous result), items, count,
 * loading, error, refetch, loadMore.
 */
const usePagedSearchQuery = ({ document, field, filter, isActive }) => {
	const [search, setSearch] = useState('');
	const [query, setQuery] = useState('');
	const queryRef = useRef(query);
	queryRef.current = query;

	useEffect(() => {
		const timeout = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
		return () => clearTimeout(timeout);
	}, [search]);

	const { data: currentData, previousData, loading, error, refetch, fetchMore } = useQuery(document, {
		variables: { page: 0, perPage: PAGE_SIZE, filter: { ...filter, ...(query ? { query } : {}) } },
		skip: !isActive,
		fetchPolicy: 'cache-and-network',
	});
	// Keep the loaded rows on screen while the next page or a new search loads.
	const data = currentData || previousData;
	const list = data?.[field];
	const items = list?.items;
	const count = list?.count || 0;
	const isFetchingMoreRef = useRef(false);

	const loadMore = useCallback(async () => {
		if (!currentData || isFetchingMoreRef.current || items.length >= count) {
			return;
		}

		isFetchingMoreRef.current = true;
		// A page that comes back after the search changed belongs to the old search.
		const pageQuery = query;
		try {
			await fetchMore({
				variables: { page: Math.floor(items.length / PAGE_SIZE) },
				updateQuery: appendFetchMorePage(field, () => queryRef.current !== pageQuery),
			});
		}
		catch (fetchError) {
			// The list keeps what it has; scrolling again retries.
		}
		finally {
			isFetchingMoreRef.current = false;
		}
	}, [count, currentData, fetchMore, field, items, query]);

	const resetSearch = useCallback(() => {
		setSearch('');
		setQuery('');
	}, []);

	return { search, setSearch, query, resetSearch, list, items, count, loading, error, refetch, loadMore };
};

export default usePagedSearchQuery;
